import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Context } from '../../../../context/AppContext';
import { SEARCH_AREA_INPUT_ID } from '../../constants';
import { useSearchContext } from '../../context/Search';
import { getNestedValue } from '../../common/utils/helpers';
import debounce from '../../common/utils/debounce';

const useSearchAreaInputLogic = () => {
  const userSuburb = getNestedValue(
    useContext(Context),
    'userLocation',
    'location',
    'name'
  );
  const {
    focusedInput: [, setFocusedInput],
    initialSearch,
    modal: [modal],
    popularSearches: { setPopularSearches },
    profileSuggest: { setProfileSuggestions },
    recentSearches: { lastSearch },
    searchAreaSuggest: {
      fetchSearchAreaSuggestions,
      setSearchAreaSuggestions,
      topSearchAreaSuggestion,
    },
    selectedSearchArea: [selectedSearchArea, setSelectedSearchArea],
    useMyLocationButtonRef,
  } = useSearchContext();
  const { formState, getFieldState, resetField, setValue } = useFormContext();
  const modalSearchArea = getNestedValue(modal, 'data', 'selectedSearchArea');
  const hasTopSearchAreaSuggestion = !!topSearchAreaSuggestion;
  const currentSearchAreaName = getNestedValue(initialSearch, 'searchAreaName');
  /**
   * Reset the selected search area and all suggestions based on the search area.
   * @returns {void}
   */
  const resetSuggestions = () => {
    setSelectedSearchArea();
    setProfileSuggestions();
    setPopularSearches();
    setSearchAreaSuggestions();
  };

  /**
   * On blur, attempt to set the selected search area to the last top search area suggestion, unless field is
   * empty. If there are no top suggestions, reset the field.
   * @param {Object} event The event object
   * @returns {void}
   */
  const handleBlur = event => {
    // Do nothing if blur is triggered by using clicking the 'use my location' button
    if (
      event.relatedTarget !== useMyLocationButtonRef.current ||
      useMyLocationButtonRef.current === null
    ) {
      const inputValue = event.target.value;
      const selectedSearchAreaName = getNestedValue(selectedSearchArea, 'name');
      if (inputValue && inputValue !== selectedSearchAreaName) {
        if (topSearchAreaSuggestion) {
          setSelectedSearchArea(topSearchAreaSuggestion);
          setValue(SEARCH_AREA_INPUT_ID, topSearchAreaSuggestion.name);
        } else {
          resetField(SEARCH_AREA_INPUT_ID);
          resetSuggestions();
        }
      }
    }
  };

  /**
   * On change, reset suggestions if the field is empty, otherwise attempt to fetch suggestions.
   * @param {Object} event The event object
   * @returns {void}
   */
  const handleChange = event => {
    const inputValue = event.target.value;
    debounce(() => {
      if (!inputValue) {
        resetSuggestions();
      } else {
        fetchSearchAreaSuggestions({ query: inputValue, debounceFetch: false });
      }
    });
  };

  /**
   * On focus, set this field as the focused input
   * @returns {void}
   */
  const handleFocus = () => {
    setFocusedInput(SEARCH_AREA_INPUT_ID);
  };

  /**
   * Attempt to set the initial value based on the following priority:
   * - Selected search area passed in from modal
   * - The category/search area page the user is on
   * - The most recent search's search area
   * - The location from app context data for the user
   */
  useEffect(() => {
    const { isDirty } = getFieldState(SEARCH_AREA_INPUT_ID, formState);
    if (modalSearchArea === null) {
      setSelectedSearchArea();
    } else if (modalSearchArea) {
      setSelectedSearchArea(modalSearchArea);
    } else if (!isDirty) {
      if (initialSearch && initialSearch.searchAreaName === '') {
        setSelectedSearchArea();
      } else if (currentSearchAreaName) {
        if (
          !hasTopSearchAreaSuggestion ||
          topSearchAreaSuggestion.name !== currentSearchAreaName
        ) {
          setValue(SEARCH_AREA_INPUT_ID, currentSearchAreaName);
          fetchSearchAreaSuggestions({
            query: currentSearchAreaName,
            debounce: false,
          });
        } else {
          setSelectedSearchArea(topSearchAreaSuggestion);
        }
      } else if (!selectedSearchArea) {
        if (lastSearch) {
          setSelectedSearchArea(lastSearch.selectedSearchArea);
        } else if (userSuburb) {
          if (!hasTopSearchAreaSuggestion) {
            setValue(SEARCH_AREA_INPUT_ID, userSuburb);
            fetchSearchAreaSuggestions({ query: userSuburb, debounce: false });
          } else {
            setSelectedSearchArea(topSearchAreaSuggestion);
          }
        }
      }
    }
  }, [
    currentSearchAreaName,
    initialSearch,
    lastSearch,
    modalSearchArea,
    !!topSearchAreaSuggestion,
    userSuburb,
  ]);

  return { handleBlur, handleChange, handleFocus };
};

export default useSearchAreaInputLogic;
